import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users,
  CheckCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Upload,
  BarChart3,
  TrendingUp,
  Settings
} from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { activitiesAPI } from '../../utils/api';
import { getErrorMessage } from '../../utils/errorTranslation';
import NotesWidget from '../../components/common/NotesWidget';
import QuickActionsWidget from '../../components/common/QuickActionsWidget';
import SharedCalendarWidget from '../../components/common/SharedCalendarWidget';
import AnalyticsView from '../../components/analytics/AnalyticsView';

const DashboardPage = () => {
  const { user } = useAuth();
  const { t, currentLanguage } = useLanguage();
  // Dashboard is free; no plan/usage gating required
  
  // State for active view (widgets or analytics)
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem('dashboardActiveView') || 'widgets';
  });
  
  // Suppression des stats statiques - remplacées par les widgets dynamiques
  
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalActivities, setTotalActivities] = useState(0);
  const activitiesPerPage = 5;

  // Handle view change
  const handleViewChange = (view) => {
    setActiveView(view);
    localStorage.setItem('dashboardActiveView', view);
  };

  // Suppression des données mock statiques
  
  // Fetch activities with pagination
  const fetchActivities = useCallback(async (page = 1) => {
    try {
      setActivitiesLoading(true);
      const response = await activitiesAPI.getActivities({ page, limit: activitiesPerPage });
      const data = response.data;
      setActivities(data.activities || []);
      setTotalPages(data.pagination?.totalPages || 0);
      setTotalActivities(data.pagination?.total || 0);
      setCurrentPage(data.pagination?.page || page);
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error(getErrorMessage(error, t, t('dashboard.errors.activitiesLoadFailed', 'Error loading activities')));
    } finally {
      setActivitiesLoading(false);
    }
  }, [activitiesPerPage]);

  useEffect(() => {
    if (user) {
      fetchActivities(1);
    }
  }, [fetchActivities, user]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchActivities(newPage);
    }
  };

  const handleRefresh = () => {
    fetchActivities(currentPage);
  };

  // Note: StatCard component supprimé - widgets statiques remplacés par des composants dynamiques

  // Function to translate activity descriptions on client side
  const translateActivity = (activity) => {
    try {
      // If the server already provided a translated description, use it
      if (activity.action_description) {
        return activity.action_description;
      }
      
      // Normalize action_type: convert dots to underscores for template lookup
      let normalizedActionType = activity.action_type;
      if (normalizedActionType && normalizedActionType.includes('.')) {
        normalizedActionType = normalizedActionType.replace(/\./g, '_');
      }
      
      // Get template based on normalized action type
      const templateKey = `dashboard.activity.templates.${normalizedActionType}`;
      let template = t(templateKey, null);
      
      // If no specific template exists, fallback to a default message
      if (!template || template === templateKey) {
        const fallback = t('dashboard.activity.fallback', '{{user}} performed an action');
        const userLabel = activity.user_name || t('dashboard.activity.userFallback', 'User');
        return fallback.replace('{{user}}', userLabel);
      }
      
      // Build parameters for replacement
      const params = {
        user: activity.user_name || t('dashboard.activity.userFallback', 'User'),
        title: activity.entity_name || '',
        filename: activity.entity_name || '',
        name: activity.entity_name || '',
      };
      
      // For analysis, extract count from metadata
      if (normalizedActionType && normalizedActionType.includes('analysis')) {
        const metadata = activity.metadata || {};
        params.count = metadata.cvs_analyzed || metadata.cvs_requested || '0';
      }
      
      // For notes shared, extract user names from metadata
      if (normalizedActionType === 'note_shared' && activity.metadata) {
        const metadata = activity.metadata;
        if (metadata.shared_with && Array.isArray(metadata.shared_with)) {
          // Extract names from array of objects
          params.userNames = metadata.shared_with
            .map(user => typeof user === 'object' ? user.name : user)
            .filter(name => name)
            .join(', ');
        } else if (metadata.shared_with_names && Array.isArray(metadata.shared_with_names)) {
          params.userNames = metadata.shared_with_names.join(', ');
        } else {
          params.userNames = '';
        }
      }
      
      // Replace all placeholders in template
      let translatedText = template;
      Object.keys(params).forEach(key => {
        const placeholder = `{{${key}}}`;
        translatedText = translatedText.replace(new RegExp(placeholder, 'g'), params[key]);
      });
      
      return translatedText;
    } catch (error) {
      console.error('Error translating activity:', error);
      // Fallback to original description
      const fallback = t('dashboard.activity.fallback', '{{user}} performed an action');
      const userLabel = activity.user_name || t('dashboard.activity.userFallback', 'User');
      return activity.action_description || fallback.replace('{{user}}', userLabel);
    }
  };

  const getDateLocale = () => {
    switch (currentLanguage) {
      case 'en':
        return enUS;
      case 'es':
        return es;
      default:
        return fr;
    }
  };

  return (
    <div className="p-6 pb-10 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {t('dashboard.welcome')} {user?.name || user?.email}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {user?.role === 'admin' 
                ? t('dashboard.subtitle.admin') 
                : t('dashboard.subtitle.hr')
              }
            </p>
          </div>
          {user?.role === 'rh' && (
            <div className="inline-flex items-center text-xs text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
              {t('dashboard.workspace.shared')}
            </div>
          )}
        </div>
      </div>

      {/* View Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 p-1 shadow-sm">
        <div className="grid grid-cols-2 gap-1">
          <button
            onClick={() => handleViewChange('widgets')}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
              activeView === 'widgets'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Settings className="h-4 w-4" />
            {t('dashboard.tabs.widgets')}
          </button>
          <button
            onClick={() => handleViewChange('analytics')}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
              activeView === 'analytics'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            {t('dashboard.tabs.analytics')}
          </button>
        </div>
      </div>

      {/* Content based on active view */}
      {activeView === 'widgets' ? (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <div className="xl:col-span-12 min-h-[340px]">
            <SharedCalendarWidget />
          </div>

          <div className="xl:col-span-6 min-h-[320px]">
            <NotesWidget />
          </div>

          <div className="xl:col-span-6 min-h-[320px]">
            <QuickActionsWidget />
          </div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="xl:col-span-12"
          >
            <div className="card rounded-lg border border-gray-200 shadow-sm">
                <div className="card-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('dashboard.activity.title')}
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                      {totalActivities > 0 ? `${totalActivities} ${t('dashboard.activity.count')}${totalActivities > 1 ? 's' : ''}` : t('dashboard.activity.noActivity')}
                    </span>
                    <button
                      onClick={handleRefresh}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                      title={t('dashboard.actions.refresh')}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="card-content">
                  {activitiesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : activities.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">{t('dashboard.activity.noRecentActivity')}</p>
                      <p className="text-xs mt-1">{t('dashboard.activity.newActionsHint')}</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                      {activities.map((activity) => {
                        // Determine icon and color based on action type
                        let Icon = CheckCircle;
                        let iconColor = 'text-gray-600';
                        
                        if (activity.action_type.startsWith('offer.')) {
                          Icon = Briefcase;
                          iconColor = 'text-blue-600';
                        } else if (activity.action_type.startsWith('cv.')) {
                          Icon = Upload;
                          iconColor = 'text-green-600';
                        } else if (activity.action_type.startsWith('analysis.')) {
                          Icon = BarChart3;
                          iconColor = 'text-purple-600';
                        } else if (activity.action_type.startsWith('candidate.')) {
                          Icon = Users;
                          iconColor = 'text-orange-600';
                        } else if (activity.action_type.startsWith('kanban.')) {
                          Icon = TrendingUp;
                          iconColor = 'text-indigo-600';
                        }
                        
                        // Format date
                        const formatDate = (dateStr) => {
                          const date = new Date(dateStr);
                          const now = new Date();
                          const diffMs = now - date;
                          const diffMins = Math.floor(diffMs / 60000);
                          const diffHours = Math.floor(diffMs / 3600000);
                          const diffDays = Math.floor(diffMs / 86400000);
                          
                          if (diffMins < 1) return t('dashboard.activity.time.now');
                          if (diffMins < 60) return t('dashboard.activity.time.minutesAgo').replace('{{count}}', diffMins).replace('{{plural}}', diffMins > 1 ? 's' : '');
                          if (diffHours < 24) return t('dashboard.activity.time.hoursAgo').replace('{{count}}', diffHours).replace('{{plural}}', diffHours > 1 ? 's' : '');
                          if (diffDays < 7) return t('dashboard.activity.time.daysAgo').replace('{{count}}', diffDays).replace('{{plural}}', diffDays > 1 ? 's' : '');
                          return format(date, 'P', { locale: getDateLocale() });
                        };
                        
                        return (
                          <div key={activity.id} className="flex items-start p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <Icon className={`h-5 w-5 ${iconColor} mr-3 mt-0.5`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">
                                <span className="font-semibold">{activity.user_name}</span>
                                <span className="text-xs text-gray-500 ml-1">({activity.user_role === 'admin' ? t('dashboard.roles.admin') : t('dashboard.roles.hr')})</span>
                                <span className="text-gray-700 ml-1"> {translateActivity(activity)}</span>
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDate(activity.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="mt-4 pt-3 border-t border-gray-200">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                            <div className="text-xs text-gray-500">
                              {t('dashboard.activity.pagination.info')
                                .replace('{{current}}', currentPage)
                                .replace('{{total}}', totalPages)
                                .replace('{{showing}}', activities.length)
                                .replace('{{total_activities}}', totalActivities)}
                            </div>
                            <div className="flex items-center space-x-1">
                              {/* Bouton Précédent */}
                              <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`p-1 rounded ${
                                  currentPage === 1 
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                } transition-colors`}
                                title={t('dashboard.activity.pagination.previous')}
                                aria-label={t('dashboard.activity.pagination.previous')}
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </button>
                              
                              {/* Numéros de pages */}
                              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = currentPage - 2 + i;
                                }
                                
                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`px-2 py-1 text-xs rounded ${
                                      pageNum === currentPage
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                    } transition-colors min-w-[24px]`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                              
                              {/* Bouton Suivant */}
                              <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={`p-1 rounded ${
                                  currentPage === totalPages 
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                } transition-colors`}
                                title={t('dashboard.activity.pagination.next')}
                                aria-label={t('dashboard.activity.pagination.next')}
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
            </div>
          </motion.div>
        </div>
      ) : (
        /* Analytics View */
        <AnalyticsView 
          user={user}
          showToast={(message, type) => {
            if (type === 'success') {
              toast.success(message);
              return;
            }
            if (type === 'info') {
              toast.info(message);
              return;
            }
            toast.error(message);
          }}
        />
      )}
    </div>
  );
};

export default DashboardPage;
